import type {
  AlarmKind,
  AlarmSeverity,
  AlarmState,
  ConfidenceLevel,
  EventOrigin,
  EventType,
  ImportStatus,
  LineStatus,
  MachineStatus,
  MachineType,
  ParameterSource,
  ProductionRunStatus,
  ReasonStatus,
  SimulationStatus,
  StopClass,
  UserRole,
} from "@opus-dt/shared-types";

export const getUserRoleLabel = (role: UserRole): string =>
  ({ GESTOR: "Gestor", OPERADOR: "Operador" })[role];

export const getLineStatusLabel = (s: LineStatus): string =>
  ({
    RUNNING: "Em produção",
    STOPPED: "Parada",
    PARTIAL: "Parcial",
    IDLE: "Ociosa",
  })[s];

export const getMachineStatusLabel = (s: MachineStatus): string =>
  ({
    RUNNING: "Em operação",
    STOPPED: "Parada",
    IDLE: "Ociosa",
    NODATA: "Sem dados",
  })[s];

export const getMachineTypeLabel = (t: MachineType): string =>
  ({ MACHINE: "Máquina", EQUIPMENT: "Equipamento", HUMAN: "Etapa humana" })[t];

export const getAlarmKindLabel = (k: AlarmKind): string =>
  ({ INFO: "Informação", ALERT: "Alerta", ALARM: "Alarme" })[k];

export const getAlarmSeverityLabel = (s: AlarmSeverity): string =>
  ({ LOW: "Baixa", MEDIUM: "Média", HIGH: "Alta" })[s];

export const getAlarmStateLabel = (s: AlarmState): string =>
  ({
    UNACKNOWLEDGED: "Não reconhecido",
    ACKNOWLEDGED: "Reconhecido",
    RESOLVED: "Resolvido",
    SHELVED: "Marcado falso",
  })[s];

export const getEventTypeLabel = (t: EventType): string =>
  ({
    STOP: "Parada",
    START: "Início",
    ALERT: "Alerta",
    MAINTENANCE: "Manutenção",
    NODATA: "Sem dados",
  })[t];

export const getEventOriginLabel = (o: EventOrigin): string =>
  ({
    SENSOR: "Sensor",
    MANUAL: "Manual",
    IMPORT: "Importação",
    INTEGRATION: "Integração",
  })[o];

export const getReasonStatusLabel = (r: ReasonStatus): string =>
  ({ PENDING: "Pendente", JUSTIFIED: "Justificado" })[r];

export const getStopClassLabel = (c: StopClass): string =>
  ({
    WITH_ALARM: "Com alarme",
    WITHOUT_ALARM: "Sem alarme",
    INFORMATIVE: "Informativa",
  })[c];

export const getProductionRunStatusLabel = (s: ProductionRunStatus): string =>
  ({ ACTIVE: "Em execução", PAUSED: "Pausada", COMPLETED: "Concluída" })[s];

export const getSimulationStatusLabel = (s: SimulationStatus): string =>
  ({
    DRAFT: "Rascunho",
    QUEUED: "Na fila",
    RUNNING: "Executando",
    COMPLETED: "Concluída",
    FAILED: "Falhou",
  })[s];

export const getConfidenceLevelLabel = (c: ConfidenceLevel): string =>
  ({
    HIGH: "Alta",
    MEDIUM: "Média",
    LOW: "Baixa",
    INSUFFICIENT: "Insuficiente",
  })[c];

export const getImportStatusLabel = (s: ImportStatus): string =>
  ({
    PENDING: "Aguardando",
    MAPPING: "Mapeando",
    VALIDATING: "Validando",
    COMPLETED: "Concluída",
    FAILED: "Falhou",
    // Spec 082 — "Desfeita" e não "Cancelada": a importação chegou a escrever, e o que ela criou
    // foi retirado. Cancelar seria não ter escrito, que é o que `FAILED` já diz.
    REVERTED: "Desfeita",
  })[s];

export function formatRelativeTime(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const diffMs = Date.now() - d.getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `há ${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `há ${min} min`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `há ${hr} h`;
  const days = Math.floor(hr / 24);
  return `há ${days} d`;
}

export function formatDateTime(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function getOeeFriendlyLabel(): { primary: string; technical: string } {
  return { primary: "Eficiência geral", technical: "OEE" };
}

export function getConfidenceFriendlyLabel(c: ConfidenceLevel): string {
  return {
    HIGH: "Confiança alta",
    MEDIUM: "Confiança média",
    LOW: "Confiança baixa",
    INSUFFICIENT: "Sem dados suficientes",
  }[c];
}

export function getMachineStatusFriendlyLabel(s: MachineStatus): string {
  return {
    RUNNING: "Em operação",
    STOPPED: "Parada",
    IDLE: "Ociosa",
    NODATA: "Sem comunicação recente",
  }[s];
}

export function getReliabilityFriendlyLabel(level: "HIGH" | "MEDIUM" | "LOW" | "SUSPECT"): string {
  return { HIGH: "Alta", MEDIUM: "Média", LOW: "Baixa", SUSPECT: "Suspeita" }[level];
}

// Motivo da confiabilidade suspeita (única causa nesta entrega: flapping).
export const ALARM_UNSTABLE_REASON = "Confiabilidade suspeita: oscilação detectada";

export function getQuantityLabel(qty: number, product?: { unit?: string | null }): string {
  const unit = product?.unit?.trim() || "un";
  return `${qty.toLocaleString("pt-BR")} ${unit}`;
}

export function getParameterSourceFriendlyLabel(s: ParameterSource): string {
  return {
    SENSOR: "Sensor",
    MANUAL: "Manual",
    IMPORT: "Importado",
    CALCULATED: "Calculado",
    INTEGRATION: "Integração",
  }[s];
}

export function getSensorStatusLabel(
  s: "ONLINE" | "OFFLINE" | "NO_READING" | "INCONSISTENT"
): string {
  return {
    ONLINE: "Online",
    OFFLINE: "Offline",
    NO_READING: "Sem leitura",
    INCONSISTENT: "Inconsistente",
  }[s];
}

export function formatRelativeAge(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "";
  const then = new Date(iso);
  const diffMs = now.getTime() - then.getTime();
  if (diffMs < 0) return "agora";
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 60) return `há ${Math.max(1, diffMin)} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `há ${diffH} h`;
  const diffDays = Math.floor(diffH / 24);
  return `há ${diffDays} ${diffDays === 1 ? "dia" : "dias"}`;
}
